import { formatBRL } from "@/lib/format";
import { getFreeShippingProgress, discountLabel } from "@/lib/commerce";
import { commercialConfig } from "@/config/storeConfig";
import { useSiteContent } from "@/lib/siteContent";
import type { CartItem, ShippingResult } from "@/types";

interface OrderSummaryCardProps {
  items: CartItem[];
  subtotal: number;
  discount: number;
  shipping: ShippingResult;
  total: number;
  showProgress?: boolean;
  deliveryMethodId?: string;
}

export default function OrderSummaryCard({
  items,
  subtotal,
  discount,
  shipping,
  total,
  showProgress = true,
  deliveryMethodId = "",
}: OrderSummaryCardProps) {
  const content = useSiteContent();
  const progress = getFreeShippingProgress(subtotal);
  const method = deliveryMethodId
    ? content.delivery.find((item) => item.id === deliveryMethodId)
    : undefined;
  const hasRuleElsewhere = content.freeShipping.some(
    (rule) => rule.enabled && !rule.methodIds.includes(deliveryMethodId as never),
  );
  const ruleNotApplicable = Boolean(method && hasRuleElsewhere && !shipping.freeApplied);

  return (
    <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
      <h2 className="font-heading text-2xl text-foreground-900">Resumo do pedido</h2>

      <div className="mt-4 flex flex-col gap-3">
        {items.map((item) => (
          <div key={item.key} className="flex items-center gap-3">
            <div className="h-16 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
              <img
                src={item.image}
                alt={`${item.name} na cor ${item.colorName}`}
                title={`${item.name} — Auréa`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-base text-foreground-900">{item.name}</p>
              <p className="text-[11px] text-foreground-500">
                {item.colorName} · Tam. {item.size} · {item.quantity}x
              </p>
            </div>
            <span className="whitespace-nowrap text-sm text-foreground-800">
              {formatBRL(item.unitPrice * item.quantity)}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-2 border-t border-background-300 pt-4 text-sm text-foreground-700">
        <div className="flex items-center justify-between">
          <span>Produtos</span>
          <span>{formatBRL(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Desconto</span>
          <span className={discount > 0 ? "text-accent-700" : ""}>
            {discount > 0 ? `-${formatBRL(discount)}` : formatBRL(0)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span>Frete</span>
          <span className={shipping.freeApplied ? "text-primary-700" : ""}>
            {shipping.fee === null ? "Taxa a confirmar" : shipping.freeApplied ? "Grátis" : formatBRL(shipping.fee)}
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-background-300 pt-3">
          <span className="font-heading text-xl text-foreground-900">Total estimado</span>
          <span className="font-heading text-xl text-primary-700">{formatBRL(total)}</span>
        </div>
      </div>

      {shipping.freeApplied ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800">
          <i className="ri-gift-line mt-0.5 text-base" />
          Você ganhou frete grátis na entrega local.
        </p>
      ) : null}

      {showProgress && ruleNotApplicable ? (
        <p className="mt-4 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-600">
          A regra de frete grátis na entrega local não se aplica à modalidade selecionada.
        </p>
      ) : showProgress && progress.hasRule && !shipping.freeApplied ? (
        <div className="mt-4">
          <p className="text-xs text-foreground-600">{progress.message}</p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-300">
            <div
              className="h-full rounded-full bg-secondary-500 transition-all"
              style={{ width: `${Math.min(100, (subtotal / (progress.remaining + subtotal || 1)) * 100)}%` }}
            />
          </div>
        </div>
      ) : null}

      {commercialConfig.discount.enabled ? (
        <p className="mt-4 text-[11px] leading-relaxed text-foreground-500">{discountLabel}</p>
      ) : null}
    </div>
  );
}